/**
 * Друкована форма інвентаризації (2026-08-11, рішення власника).
 *
 * Два режими за станом документа:
 * - DRAFT/IN_PROGRESS — «Інвентаризаційний опис»: книжкові кількості +
 *   ПОРОЖНЯ колонка «Фактично» (комісія вписує від руки і підписує акт).
 * - POSTED — «Акт інвентаризації»: книжково/фактично/розбіжність,
 *   суми надлишків і недостач.
 * Підписи: голова та члени комісії, МВО.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inventoryCounts } from '@/api/inventoryCounts';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InventoryCountPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['inventory-count', id],
    queryFn: () => inventoryCounts.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }

  const isAct = d.posted;
  const whName = (whQ.data ?? []).find((w) => w.id === d.warehouse_id)?.name ?? '—';
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));

  const totBook = d.items.reduce((s, it) => s + Number(it.book_quantity ?? 0), 0);
  const totFact = d.items.reduce((s, it) => s + Number(it.fact_quantity ?? 0), 0);
  const surplus = d.items.filter((it) =>
    Number(it.fact_quantity) > Number(it.book_quantity)).length;
  const shortage = d.items.filter((it) =>
    Number(it.fact_quantity) < Number(it.book_quantity)).length;

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .inv-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .inv-table th, .inv-table td { border: 1px solid #000; padding: 1px 4px; }
        .inv-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.inventory_counts',
        documentId: d.id,
        filename: `Інвентаризація ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0 0.25rem' }}>
        {isAct ? 'Акт інвентаризації' : 'Інвентаризаційний опис'} № {d.document_number}
      </h2>
      <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '10pt', color: '#555' }}>
        станом на {new Date(d.inventory_date).toLocaleDateString('uk-UA')}
        {' · '}склад: <strong>{whName}</strong>
      </div>

      <table className="inv-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування</th>
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>За обліком</th>
            <th style={{ width: 80 }}>Фактично</th>
            {isAct && <th style={{ width: 90 }}>Розбіжність</th>}
            {isAct && <th style={{ width: 90 }}>Сума за обліком</th>}
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            const diff = Number(it.fact_quantity) - Number(it.book_quantity);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  {nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'шт') : 'шт'}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(it.book_quantity ?? 0)}</td>
                <td style={{ textAlign: 'right' }}>
                  {isAct ? fmtQty(it.fact_quantity ?? 0) : ''}
                </td>
                {isAct && (
                  <td style={{ textAlign: 'right',
                    fontWeight: diff !== 0 ? 700 : 400,
                    color: diff > 0 ? '#15803d' : diff < 0 ? '#b91c1c' : undefined }}>
                    {diff === 0 ? '—' : (diff > 0 ? '+' : '') + fmtQty(diff)}
                  </td>
                )}
                {isAct && (
                  <td style={{ textAlign: 'right' }}>{fmtMoney(it.book_amount ?? 0)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmtQty(totBook)}</td>
            <td style={{ textAlign: 'right' }}>{isAct ? fmtQty(totFact) : ''}</td>
            {isAct && <td></td>}
            {isAct && <td></td>}
          </tr>
        </tfoot>
      </table>

      {isAct ? (
        <p style={{ margin: '0.5rem 0' }}>
          Всього позицій {d.items.length}; з надлишком — {surplus}, з
          недостачею — {shortage}. Надлишки оприбутковано (Дт 281 — Кт 719),
          недостачі списано (Дт 947 — Кт 281) проведенням цього акта.
        </p>
      ) : (
        <p style={{ margin: '0.5rem 0' }}>
          Всього позицій {d.items.length}. Комісія перевіряє фактичну
          наявність, вписує кількості у колонку «Фактично» та підписує опис.
        </p>
      )}
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Голова комісії</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
              <br /><br />
              <strong>Члени комісії</strong><br /><br />
              _______________________<br /><br />
              _______________________
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Матеріально відповідальна особа</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
